import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { getDistrictLogoSrc } from '../../utils/apiUrl';
import SuccessPopup from '../shared/SuccessPopup';
import '../superadmin/AddDistrict.css';

const EditDistrictInfo = () => {
  const { user, refreshUser } = useAuth();
  const [formData, setFormData] = useState({
    primary_color: '#3498db',
    secondary_color: '#2ecc71',
    primary_contact: '',
    contact_phone: '',
    contact_email: '',
    map_center: { lat: '', lng: '', address: '', city: '', state: '', zip: '' },
    district_boundary: '',
    returning_enrollment_source: 'in_app_rollover',
    parent_submissions_locked: false,
    parent_edits_locked: false,
    active_enrollment_school_year: '',
    county_district_code: '',
    migrant_contact: ''
  });
  const [logo, setLogo] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [saveFeedback, setSaveFeedback] = useState(null);
  const [districtDataLoaded, setDistrictDataLoaded] = useState(false);

  useEffect(() => {
    if (user && !districtDataLoaded) {
      fetchDistrictData();
    }
  }, [user, districtDataLoaded]);

  const fetchDistrictData = async () => {
    try {
      const response = await axios.get('/api/districts/current');
      const district = response.data;

      // Handle backward compatibility for map_center structure
      let mapCenter = { lat: '', lng: '', address: '', city: '', state: '', zip: '' };
      if (district.map_center) {
        if (typeof district.map_center === 'object') {
          mapCenter = {
            lat: district.map_center.lat || '',
            lng: district.map_center.lng || '',
            address: district.map_center.address || '',
            city: district.map_center.city || '',
            state: district.map_center.state || '',
            zip: district.map_center.zip || ''
          };
        }
      }

      setFormData({
        primary_color: user.primary_color || '#3498db',
        secondary_color: user.secondary_color || '#2ecc71',
        primary_contact: user.primary_contact || '',
        contact_phone: user.contact_phone || '',
        contact_email: user.contact_email || '',
        map_center: mapCenter,
        district_boundary: district.district_boundary ? JSON.stringify(district.district_boundary, null, 2) : '',
        returning_enrollment_source: district.returning_enrollment_source || 'in_app_rollover',
        parent_submissions_locked: !!district.parent_submissions_locked,
        parent_edits_locked: !!district.parent_edits_locked,
        active_enrollment_school_year: district.active_enrollment_school_year || '',
        county_district_code: district.county_district_code || '',
        migrant_contact: district.migrant_contact || ''
      });
      setDistrictDataLoaded(true);
    } catch (error) {
      console.error('Failed to fetch district data:', error);
      // Fallback to user data if district data fails
      setFormData({
        primary_color: user.primary_color || '#3498db',
        secondary_color: user.secondary_color || '#2ecc71',
        primary_contact: user.primary_contact || '',
        contact_phone: user.contact_phone || '',
        contact_email: user.contact_email || '',
        map_center: { lat: '', lng: '', address: '', city: '', state: '', zip: '' },
        district_boundary: '',
        returning_enrollment_source: 'in_app_rollover',
        parent_submissions_locked: false,
        parent_edits_locked: false,
        active_enrollment_school_year: '',
        county_district_code: '',
        migrant_contact: ''
      });
      setDistrictDataLoaded(true);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleMapCenterChange = (field, value) => {
    setFormData({
      ...formData,
      map_center: {
        ...formData.map_center,
        [field]: value
      }
    });
  };

  const handleBoundaryChange = (e) => {
    const value = e.target.value;
    setFormData({
      ...formData,
      district_boundary: value
    });

    // Clear any previous boundary-related errors when user starts typing
    if (error && error.includes('district boundary')) {
      setError('');
    }
  };

  // Geocode address to coordinates using backend proxy (axios uses baseURL for production)
  const geocodeAddress = async (addressData) => {
    const { address, city, state, zip } = addressData;
    const parts = [address, city, state, zip].filter(part => part && part.trim());
    const addressString = parts.join(', ');

    if (!addressString.trim()) return null;

    try {
      const response = await axios.get(
        `/api/form-submissions/geocode?q=${encodeURIComponent(addressString)}`
      );

      const data = response.data;

      if (data && data.length > 0) {
        const result = data[0];
        return {
          lat: parseFloat(result.lat),
          lng: parseFloat(result.lon)
        };
      }

      return null;
    } catch (error) {
      console.error('Geocoding error:', error);
      return null;
    }
  };

  const handleFileChange = (e) => {
    setLogo(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaveFeedback(null);
    setLoading(true);

    // Validate map center data
    const { map_center } = formData;
    const hasCoordinates = map_center.lat && map_center.lng;
    const hasAddress = map_center.address && map_center.address.trim();
    const hasCity = map_center.city && map_center.city.trim();

    // If coordinates are entered, prefer them (no address validation needed)
    if (hasCoordinates) {
      // Coordinates take precedence, no additional validation needed
    } else if (hasAddress) {
      // If address is provided, require city, state, and zip
      if (!hasCity || !map_center.state || !map_center.zip) {
        setError('If you enter a street address, you must also provide City, State, and Zip Code');
        setLoading(false);
        return;
      }
    } else if (hasCity) {
      // If only city is provided, require state and zip
      if (!map_center.state || !map_center.zip) {
        setError('If you enter a city, you must also provide State and Zip Code');
        setLoading(false);
        return;
      }
    } else {
      // No location data provided
      setError('Please provide either coordinates (lat/lng) or location information (city/state/zip)');
      setLoading(false);
      return;
    }

    // If no coordinates provided, try to geocode the address
    let finalMapCenter = { ...map_center };
    if (!hasCoordinates) {
      setError(''); // Clear any previous errors
      setLoading(true); // Keep loading state while geocoding

      // Show geocoding status
      const originalButtonText = 'Saving...';
      // Note: We could add a status message here if needed

      try {
        const geocodedCoords = await geocodeAddress(map_center);
        if (geocodedCoords) {
          finalMapCenter.lat = geocodedCoords.lat;
          finalMapCenter.lng = geocodedCoords.lng;
          console.log('Successfully geocoded address to coordinates:', geocodedCoords);
        } else {
          setError('Could not geocode the provided address. Please enter coordinates manually or check the address format.');
          setLoading(false);
          return;
        }
      } catch (error) {
        setError('Failed to geocode address. Please enter coordinates manually.');
        setLoading(false);
        return;
      }
    }

    try {
      const formDataToSend = new FormData();

      // Handle regular fields
      Object.keys(formData).forEach(key => {
        if (key === 'map_center') {
          formDataToSend.append(key, JSON.stringify(finalMapCenter));
        } else if (key === 'district_boundary') {
          if (formData[key].trim()) {
            try {
              const parsed = JSON.parse(formData[key]);
              formDataToSend.append(key, JSON.stringify(parsed));
            } catch (e) {
              setError(`Invalid JSON format for district boundary: ${e.message}`);
              setLoading(false);
              return;
            }
          }
        } else if (key === 'parent_submissions_locked' || key === 'parent_edits_locked') {
          formDataToSend.append(key, formData[key] ? 'true' : 'false');
        } else {
          formDataToSend.append(key, formData[key] ?? '');
        }
      });

      if (logo) {
        formDataToSend.append('logo', logo);
      }

      await axios.put('/api/districts/my-district/info', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setSaveFeedback({
        variant: 'success',
        title: 'Updated',
        message: 'District information was saved successfully.'
      });
      // Refresh user data to get updated logo_url and district info
      if (refreshUser) {
        try {
          await refreshUser();
          console.log('[EditDistrictInfo] User data refreshed with new logo');
        } catch (err) {
          console.error('[EditDistrictInfo] Failed to refresh user data:', err);
          // Fallback to page reload if refresh fails
          setTimeout(() => {
            window.location.reload();
          }, 1500);
        }
      } else {
        // Fallback if refreshUser not available
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      }
    } catch (error) {
      setSaveFeedback({
        variant: 'error',
        title: 'Update failed',
        message: error.response?.data?.error || 'Failed to update district info'
      });
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return <div>Loading...</div>;
  }

  return (
    <div className="add-district">
      <h2>Edit School Information</h2>
      {error && <div className="error-message">{error}</div>}
      <SuccessPopup
        show={!!saveFeedback}
        onClose={() => setSaveFeedback(null)}
        variant={saveFeedback?.variant || 'success'}
        title={saveFeedback?.title}
        message={saveFeedback?.message || ''}
        autoCloseDelay={saveFeedback?.variant === 'error' ? 0 : 5000}
      />
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Logo (JPG, PNG, WEBP)</label>
          <input
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            onChange={handleFileChange}
          />
          {user.logo_url && !logo && (
            <small>Current logo: <a href={getDistrictLogoSrc(user)} target="_blank" rel="noopener noreferrer">View</a></small>
          )}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Primary Color</label>
            <input
              type="color"
              name="primary_color"
              value={formData.primary_color}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label>Secondary Color</label>
            <input
              type="color"
              name="secondary_color"
              value={formData.secondary_color}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="form-group">
          <label>Primary Contact *</label>
          <input
            type="text"
            name="primary_contact"
            value={formData.primary_contact}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group" style={{ borderTop: '1px solid #e3e8ee', paddingTop: '1rem', marginTop: '0.5rem' }}>
          <label>County-District Code</label>
          <input
            type="text"
            name="county_district_code"
            value={formData.county_district_code}
            onChange={handleChange}
            maxLength={20}
            placeholder="e.g. 015-123"
          />
          <small style={{ color: '#6b7a8c' }}>
            Your district's official Missouri DESE county-district code. It is printed in the header of the
            Migrant Education Parent Questionnaire (MO 500-3129) filed with the state. Find it on any DESE
            report or in the DESE school directory — do not guess. Leave blank to print nothing.
          </small>
        </div>

        <div className="form-group">
          <label>District Migrant Contact</label>
          <input
            type="text"
            name="migrant_contact"
            value={formData.migrant_contact}
            onChange={handleChange}
            maxLength={255}
            placeholder="Staff member's name"
          />
          <small style={{ color: '#6b7a8c' }}>
            The staff member the state contacts about Title I.C migrant education. Printed in the questionnaire
            header. Saving this updates the header on every questionnaire, including ones already submitted.
          </small>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Contact Phone *</label>
            <input
              type="tel"
              name="contact_phone"
              value={formData.contact_phone}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label>Contact Email *</label>
            <input
              type="email"
              name="contact_email"
              value={formData.contact_email}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>Enrollment &amp; parent access</h3>
        <p style={{ color: '#6c757d', marginBottom: '1rem', fontSize: '0.95rem' }}>
          Returning student mode and parent submission/edit locks apply district-wide. When you change <strong>Active enrollment school year</strong>, parents keep seeing students who already finished a prior year: new-year intake rows are created for them automatically; they use <strong>Returning Student</strong> (prefilled from last year) and cannot edit last year&apos;s submission from the parent portal.
        </p>
        <div className="form-group">
          <label htmlFor="returning_enrollment_source">Returning student enrollment</label>
          <select
            id="returning_enrollment_source"
            name="returning_enrollment_source"
            value={formData.returning_enrollment_source}
            onChange={handleChange}
          >
            <option value="in_app_rollover">Returning prior enrolled (prefill from this system — default)</option>
            <option value="csv_import">Returning by CSV import (legacy migration flow)</option>
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="active_enrollment_school_year">Active enrollment school year (label)</label>
          <input
            type="text"
            id="active_enrollment_school_year"
            name="active_enrollment_school_year"
            value={formData.active_enrollment_school_year}
            onChange={handleChange}
            placeholder="e.g. 2025-2026"
            maxLength={32}
          />
          <small style={{ color: '#666', display: 'block', marginTop: '0.25rem' }}>
            Required for parents to add students: new intakes are stored under this school year, and parents only see students
            for this year. Use a label like 2025-2026 and keep it in sync when you roll forward each year.
          </small>
        </div>
        <div className="form-group form-group-checkbox-row">
          <input
            type="checkbox"
            id="parent_submissions_locked"
            name="parent_submissions_locked"
            checked={formData.parent_submissions_locked}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, parent_submissions_locked: e.target.checked }))
            }
          />
          <label htmlFor="parent_submissions_locked">
            Lock new parent submissions (district-wide)
          </label>
        </div>
        <div className="form-group form-group-checkbox-row">
          <input
            type="checkbox"
            id="parent_edits_locked"
            name="parent_edits_locked"
            checked={formData.parent_edits_locked}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, parent_edits_locked: e.target.checked }))
            }
          />
          <label htmlFor="parent_edits_locked">
            Lock parent edits to submitted forms (district-wide)
          </label>
        </div>

        <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>Map Settings</h3>

        <div className="form-group">
          <label>Street Address (optional)</label>
          <input
            type="text"
            value={formData.map_center.address || ''}
            onChange={(e) => handleMapCenterChange('address', e.target.value)}
            placeholder="e.g., 123 Main St"
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>City *</label>
            <input
              type="text"
              value={formData.map_center.city || ''}
              onChange={(e) => handleMapCenterChange('city', e.target.value)}
              placeholder="e.g., Van Buren"
              required
            />
          </div>
          <div className="form-group">
            <label>State *</label>
            <input
              type="text"
              value={formData.map_center.state || ''}
              onChange={(e) => handleMapCenterChange('state', e.target.value)}
              placeholder="e.g., MO"
              maxLength="2"
              required
            />
          </div>
          <div className="form-group">
            <label>Zip Code *</label>
            <input
              type="text"
              value={formData.map_center.zip || ''}
              onChange={(e) => handleMapCenterChange('zip', e.target.value)}
              placeholder="e.g., 63965"
              maxLength="10"
              required
            />
          </div>
        </div>

        <div style={{ marginTop: '1rem', marginBottom: '1rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '4px', border: '1px solid #dee2e6' }}>
          <p style={{ margin: '0 0 0.5rem 0', fontWeight: 'bold', color: '#495057' }}>Coordinates (Optional - takes precedence over address)</p>
          <p style={{ margin: '0 0 1rem 0', fontSize: '0.9em', color: '#6c757d' }}>
            If you enter coordinates, they will be used instead of the address information above.
          </p>
          <div className="form-row">
            <div className="form-group">
              <label>Latitude</label>
              <input
                type="number"
                step="0.000001"
                value={formData.map_center.lat || ''}
                onChange={(e) => handleMapCenterChange('lat', e.target.value)}
                placeholder="e.g., 32.9312"
              />
            </div>
            <div className="form-group">
              <label>Longitude</label>
              <input
                type="number"
                step="0.000001"
                value={formData.map_center.lng || ''}
                onChange={(e) => handleMapCenterChange('lng', e.target.value)}
                placeholder="e.g., -96.4597"
              />
            </div>
          </div>
        </div>

        <div className="form-group">
          <label>District Boundary (GeoJSON)</label>
          <textarea
            value={formData.district_boundary}
            onChange={handleBoundaryChange}
            placeholder='Paste GeoJSON boundary data here (optional)'
            rows={8}
            style={{ fontFamily: 'monospace', fontSize: '0.9em' }}
          />
          <small style={{ color: '#666', display: 'block', marginTop: '0.25rem' }}>
            Optional: Paste valid GeoJSON to define district boundaries. Examples:
            <br />• Simple polygon: <code style={{background: '#f8f9fa', padding: '2px 4px', borderRadius: '3px'}}>{"{ \"type\": \"Feature\", \"properties\": {}, \"geometry\": { \"type\": \"Polygon\", \"coordinates\": [[[lng1,lat1],[lng2,lat2],[lng3,lat3],[lng1,lat1]]] } }"}</code>
            <br />• FeatureCollection: <code style={{background: '#f8f9fa', padding: '2px 4px', borderRadius: '3px'}}>{"{ \"type\": \"FeatureCollection\", \"features\": [{ \"type\": \"Feature\", \"properties\": {}, \"geometry\": { \"type\": \"Polygon\", \"coordinates\": [[[lng,lat]]] } }] }"}</code>
            <br />💡 Tip: Use tools like <a href="https://geojson.io" target="_blank" rel="noopener noreferrer">geojson.io</a> to create valid GeoJSON boundaries
            <br />📍 Van Buren Example Template: Replace LNG,LAT with your actual coordinates
            <br /><code style={{background: '#f8f9fa', padding: '2px 4px', borderRadius: '3px', fontSize: '0.8em'}}>{"{ \"type\": \"Feature\", \"properties\": { \"name\": \"Van Buren R-1 District\" }, \"geometry\": { \"type\": \"Polygon\", \"coordinates\": [[[-90.9300,36.9800],[-90.8500,36.9800],[-90.8500,37.0500],[-90.9300,37.0500],[-90.9300,36.9800]]] } }"}</code>
          </small>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Updating...' : 'Update Information'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditDistrictInfo;

